import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, Wrench, Bug } from 'lucide-react';
import FlagToggle from '../components/FlagToggle';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { track } from '../lib/analytics';
import { CHANGELOG } from '../data/changelog';

const TYPE_META = {
  shipped: { icon: Sparkles, classPl: 'Nowość', classEn: 'New' },
  improved: { icon: Wrench, classPl: 'Ulepszenie', classEn: 'Improved' },
  fixed: { icon: Bug, classPl: 'Naprawione', classEn: 'Fixed' },
};

const CoNowego = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isEn = i18n.resolvedLanguage?.startsWith('en');

  useEffect(() => {
    track('page_view', { page: 'co_nowego' });
    document.title = t('changelog.metaTitle');
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', t('changelog.metaDescription'));
  }, [t]);

  // Group entries by month (e.g. "Luty 2026" / "February 2026")
  const groups = useMemo(() => {
    const map = new Map();
    CHANGELOG.forEach((entry) => {
      const d = new Date(entry.date);
      const monthLabel = d.toLocaleDateString(isEn ? 'en-GB' : 'pl-PL', { year: 'numeric', month: 'long' });
      if (!map.has(monthLabel)) map.set(monthLabel, []);
      map.get(monthLabel).push(entry);
    });
    return Array.from(map.entries());
  }, [isEn]);

  // JSON-LD ItemList for SEO
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": isEn ? "HussarAi changelog" : "Co nowego w HussarAi",
    "url": "https://hussarai.org/co-nowego",
    "itemListElement": CHANGELOG.map((e, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": isEn ? e.titleEn : e.titlePl,
      "datePublished": e.date,
      "url": `https://hussarai.org/co-nowego#${e.slug}`,
    })),
  };

  return (
    <div className="hussar-landing" data-testid="co-nowego-page">
      {/* JSON-LD structured data — safe-by-design: content is a local
          component constant (no user input), JSON.stringify produces valid
          JSON, and `<` is escaped to \u003c to prevent any script-tag
          breakout. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c') }}
      />
      <header className="hussar-landing-header">
        <div className="hussar-landing-header-content">
          <Link to="/" className="hussar-logo-full">
            <img src="/hussarai-logo-full.png" alt="HussarAi" className="hussar-logo-full-img-small" />
          </Link>
          <div className="hussar-landing-header-actions">
            <FlagToggle />
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              data-testid="changelog-nav-login"
            >
              {t('agenci.nav.login')}
            </Button>
            <Button
              onClick={() => navigate('/register')}
              className="hussar-btn-primary"
              data-testid="changelog-nav-register"
            >
              {t('agenci.nav.start')}
            </Button>
          </div>
        </div>
      </header>

      <main className="hussar-agenci-main">
        <section className="hussar-agenci-hero" data-testid="changelog-hero">
          <div className="hussar-agenci-eyebrow">{t('changelog.eyebrow')}</div>
          <h1 className="hussar-agenci-h1" data-testid="changelog-h1">{t('changelog.h1')}</h1>
          <p className="hussar-agenci-subhead">{t('changelog.subhead')}</p>
        </section>

        <section className="hussar-changelog-list" data-testid="changelog-list">
          {groups.map(([monthLabel, entries]) => (
            <div key={monthLabel} className="hussar-changelog-group">
              <h2 className="hussar-changelog-month" data-testid={`changelog-month-${monthLabel.replace(/\s+/g, '-').toLowerCase()}`}>
                {monthLabel}
              </h2>
              <div className="hussar-changelog-entries">
                {entries.map((entry) => {
                  const meta = TYPE_META[entry.type] || TYPE_META.shipped;
                  const Icon = meta.icon;
                  const title = isEn ? entry.titleEn : entry.titlePl;
                  const body = isEn ? entry.bodyEn : entry.bodyPl;
                  const typeLabel = isEn ? meta.classEn : meta.classPl;
                  return (
                    <article
                      key={entry.slug}
                      id={entry.slug}
                      className="hussar-changelog-entry"
                      data-testid={`changelog-entry-${entry.slug}`}
                    >
                      <div className="hussar-changelog-entry-meta">
                        <span className={`hussar-changelog-type hussar-changelog-type-${entry.type}`}>
                          <Icon className="w-3 h-3" />
                          {typeLabel}
                        </span>
                        <time className="hussar-changelog-date" dateTime={entry.date}>
                          {new Date(entry.date).toLocaleDateString(isEn ? 'en-GB' : 'pl-PL', { day: 'numeric', month: 'long' })}
                        </time>
                      </div>
                      <h3 className="hussar-changelog-title">{title}</h3>
                      <p className="hussar-changelog-body">{body}</p>
                      {entry.tags?.length > 0 && (
                        <div className="hussar-changelog-tags">
                          {entry.tags.map((tag) => (
                            <span key={tag} className="hussar-changelog-tag">#{tag}</span>
                          ))}
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            </div>
          ))}
        </section>

        <section className="hussar-agenci-mid-cta" data-testid="changelog-cta">
          <h2 className="hussar-agenci-mid-cta-title">{t('changelog.ctaTitle')}</h2>
          <p className="hussar-agenci-mid-cta-body">{t('changelog.ctaBody')}</p>
          <Button
            size="lg"
            onClick={() => navigate('/register')}
            className="hussar-btn-primary hussar-btn-large"
            data-testid="changelog-cta-register"
          >
            {t('changelog.ctaButton')}
          </Button>
        </section>
      </main>

      <footer className="hussar-landing-footer">
        <div className="hussar-landing-footer-content">
          <div className="hussar-landing-footer-links">
            <Link to="/">{t('agenci.footer.home')}</Link>
            <Link to="/agenci-ai-dla-msp">{t('agenci.footer.agenci')}</Link>
            <Link to="/vs/bielik">{t('agenci.footer.vsBielik')}</Link>
            <Link to="/pricing">{t('agenci.footer.pricing')}</Link>
          </div>
          <div className="hussar-landing-footer-meta">© 2026 HussarAi</div>
        </div>
      </footer>
    </div>
  );
};

export default CoNowego;
